import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PermissionAuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      // Fetch recent user profile updates as audit trail
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role, updated_at')
        ?.order('updated_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;

      setAuditLogs(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: 'Shield',
      scout: 'Search',
      club: 'Building',
      player: 'User',
      coach: 'Clipboard'
    };
    return icons?.[role] || 'User';
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-error',
      scout: 'text-accent',
      club: 'text-warning',
      player: 'text-primary',
      coach: 'text-secondary'
    };
    return colors?.[role] || 'text-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Permission Audit Log</h3>
            <p className="text-sm text-muted-foreground">Recent administrative actions and role changes</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : auditLogs?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No audit logs available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {auditLogs?.map((log, index) => (
              <div 
                key={`${log?.id}-${index}`}
                className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className={`w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 ${getRoleColor(log?.role)}`}>
                  <Icon name={getRoleIcon(log?.role)} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground">
                      {log?.full_name}
                    </p>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(log?.updated_at)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{log?.email}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Current Role:</span>
                    <span className={`text-xs font-semibold ${getRoleColor(log?.role)}`}>
                      {log?.role?.charAt(0)?.toUpperCase() + log?.role?.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionAuditLog;